import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  ChoiceList,
  List,
  Spinner,
} from '@shopify/polaris';
import {
  useSnapchatConfig,
  useConfigureSnapchat,
  useToggleSnapchat,
  useTestSnapchat,
  useDisconnectSnapchat,
  useConnectSnapchat,
  useSnapchatAssets,
  useSelectSnapchatAsset,
} from '../hooks/api';
import { useLanguage, useTranslation } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { openOAuthWindow, navigateOAuth } from '../lib/auth';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';
import { GateBanner, type PageError } from '../components/GateBanner';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">Connected</Badge>;
    case 'warning':
      return <Badge tone="warning">Warning</Badge>;
    case 'error':
      return <Badge tone="critical">Error</Badge>;
    case 'disabled':
      return <Badge>Disabled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export function Snapchat() {
  const { data: config, isLoading } = useSnapchatConfig();
  const configureMutation = useConfigureSnapchat();
  const toggleMutation = useToggleSnapchat();
  const testMutation = useTestSnapchat();
  const disconnectMutation = useDisconnectSnapchat();
  const { language } = useLanguage();
  const t = useTranslation();

  const connectMutation = useConnectSnapchat();
  const selectAssetMutation = useSelectSnapchatAsset();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState('');
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [testEventCode, setTestEventCode] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<PageError>(null);
  const [actionError, setActionError] = useState<PageError>(null);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);
  const [connectError, setConnectError] = useState<PageError>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      setConnectError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The Snapchat connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The Snapchat connection could not be completed (${oauthErr.replace(/^[a-z0-9_]*?_/, '')}). Please try again.`
      );
    }
    if (params.get('step') === 'select-pixel' || params.get('snapchat') === 'connected') {
      setShowPicker(true);
    }
  }, []);

  const assetsQuery = useSnapchatAssets(showPicker);

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    const win = openOAuthWindow();
    try {
      const { url } = await connectMutation.mutateAsync();
      navigateOAuth(win, url);
    } catch (error: any) {
      win?.close();
      setConnectError(error?.message ? error : 'Could not start the Snapchat connection.');
    }
  }, [connectMutation]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedPixel('');
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    params.delete('snapchat');
    params.delete('platform');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }, []);

  const handleSelectAsset = useCallback(async () => {
    if (!selectedPixel) return;
    try {
      await selectAssetMutation.mutateAsync({ pixelId: selectedPixel });
      closePicker();
    } catch (error: any) {
      setConnectError(error?.message ? error : 'Could not finish the connection.');
    }
  }, [selectedPixel, selectAssetMutation, closePicker]);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    if (!pixelId || !accessToken) {
      setConfigError('Pixel ID and Access Token are required.');
      return;
    }

    try {
      await configureMutation.mutateAsync({
        pixelId,
        accessToken,
        testEventCode: testEventCode || undefined,
      });
      setShowConfigModal(false);
      setPixelId('');
      setAccessToken('');
      setTestEventCode('');
    } catch (error: any) {
      setConfigError(error?.message ? error : t.common.configFailed);
    }
  }, [pixelId, accessToken, testEventCode, configureMutation]);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error?.message ? error : t.common.actionFailed);
      }
    },
    [toggleMutation]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      const result: any = await testMutation.mutateAsync();
      setTestResult({
        success: true,
        message: result.message || 'Test event sent successfully.',
      });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Test event failed.' });
    }
  }, [testMutation]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error?.message ? error : t.common.actionFailed);
    }
  }, [disconnectMutation]);

  if (isLoading) {
    return (
      <Page title="Snapchat Conversions API">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Snapchat Conversions API"
      subtitle="Server-side conversion tracking for Snapchat Ads"
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? 'Disable Tracking' : 'Enable Tracking',
              onAction: () => handleToggle(!config.isEnabled),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <GateBanner error={actionError} onDismiss={() => setActionError(null)} />
          </Layout.Section>
        )}

        {/* Connect Error Banner */}
        {connectError && (
          <Layout.Section>
            <GateBanner error={connectError} onDismiss={() => setConnectError(null)} />
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title="Warning">
              <p>{config.lastErrorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Connection Status
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      Pixel ID:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.pixelId}
                    </Text>
                  </InlineStack>

                  {config.testEventCode && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Test Event Code:
                      </Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {config.testEventCode}
                      </Text>
                    </InlineStack>
                  )}

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Last Sync:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      Send Test Event
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Update Configuration
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      Disconnect
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  {/* Only offer one-click when the Snapchat developer app is
                      actually configured. Before this, the button was always
                      shown and sent the merchant to Snapchat with an empty
                      client_id, landing them on Snapchat's error page. */}
                  <Text as="p" variant="bodyMd">
                    {config?.oauthAvailable
                      ? 'Connect Snapchat in one click - we list your Snap Pixels and set everything up, no Pixel ID or token to copy. Server-side tracking stays reliable even when client-side tracking is blocked by ad blockers or browser privacy features.'
                      : 'Add your Snap Pixel ID and Conversions API token to start sending server-side conversions. Server-side tracking stays reliable even when client-side tracking is blocked by ad blockers or browser privacy features.'}
                  </Text>
                  <InlineStack gap="200">
                    {config?.oauthAvailable ? (
                      <>
                        <Button variant="primary" onClick={handleConnect} loading={connectMutation.isPending}>
                          Connect Snapchat
                        </Button>
                        <Button onClick={() => setShowConfigModal(true)}>
                          Advanced: enter manually
                        </Button>
                      </>
                    ) : (
                      <Button variant="primary" onClick={() => setShowConfigModal(true)}>
                        Set up Snapchat
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Manual-path instructions ONLY when one-click is unavailable: they tell
            the merchant to generate and paste a Conversions API token, which the
            OAuth path never asks for. Same fix as Pinterest. */}
        {!config?.connected && !config?.oauthAvailable && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Setup Instructions
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    1. Go to Snapchat Ads Manager &gt; Events Manager and select your Snap Pixel.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    2. Copy your Pixel ID from the pixel settings.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    3. Generate a Conversions API Token under the "Conversions API" section.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    4. Paste both values below and click "Save Configuration".
                  </Text>
                  <Text as="p" variant="bodyMd">
                    5. Send a test event to verify the connection is working.
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Configuration Modal */}
        <Modal
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigError(null);
          }}
          title="Configure Snapchat Conversions API"
          primaryAction={{
            content: 'Save Configuration',
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowConfigModal(false);
                setConfigError(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && (
                <GateBanner error={configError} />
              )}

              <TextField
                label="Pixel ID"
                value={pixelId}
                onChange={setPixelId}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
                helpText="Found in Snapchat Ads Manager > Events Manager > your pixel."
              />

              <TextField
                label="Access Token"
                value={accessToken}
                onChange={setAccessToken}
                type="password"
                placeholder="Enter your Conversions API token"
                autoComplete="off"
                helpText="Generate one in Events Manager > Conversions API > Generate Token."
              />

              <TextField
                label="Test Event Code (optional)"
                value={testEventCode}
                onChange={setTestEventCode}
                placeholder="e.g., TEST12345"
                autoComplete="off"
                helpText="Use a test event code to verify events without affecting production data."
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Pixel Picker Modal (after one-click Connect) */}
        <Modal
          open={showPicker}
          onClose={closePicker}
          title="Choose your Snapchat Pixel"
          primaryAction={{
            content: 'Finish setup',
            onAction: handleSelectAsset,
            loading: selectAssetMutation.isPending,
            disabled: !selectedPixel,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: closePicker }]}
        >
          <Modal.Section>
            {assetsQuery.isLoading ? (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" accessibilityLabel="Loading pixels" />
                <Text as="span" variant="bodyMd">Loading your Snapchat pixels...</Text>
              </InlineStack>
            ) : assetsQuery.isError ? (
              <Banner tone="critical">
                <p>Could not load your pixels. The connection may have expired - click Connect again.</p>
              </Banner>
            ) : (assetsQuery.data?.pixels.length ?? 0) === 0 ? (
              /*
                Name what we looked at, do not just say we found nothing.
                "No pixels" has two very different causes and the merchant
                cannot tell them apart: either this advertiser genuinely has no
                pixel, or they authorized an organization that does not hold it.
                The second is the one that happens in practice - it happened on
                our own first run - and it is fixed by reconnecting with the
                right account, not by creating anything.
              */
              <Banner tone="warning" title="No Snap Pixel found">
                <BlockStack gap="200">
                  {(assetsQuery.data?.accounts?.length ?? 0) > 0 ? (
                    <>
                      <Text as="p" variant="bodyMd">
                        We looked at {assetsQuery.data!.accounts.length} ad account
                        {assetsQuery.data!.accounts.length > 1 ? 's' : ''} and none of them has a
                        pixel:
                      </Text>
                      <List type="bullet">
                        {assetsQuery.data!.accounts.map((a) => (
                          <List.Item key={`${a.orgName}/${a.adAccountName}`}>
                            {a.adAccountName} ({a.orgName})
                          </List.Item>
                        ))}
                      </List>
                      <Text as="p" variant="bodyMd">
                        If your pixel lives somewhere else, reconnect and pick that organization.
                        Otherwise create one in{' '}
                        <a href="https://business.snapchat.com/" target="_blank" rel="noopener noreferrer">
                          Snapchat Business Manager
                        </a>{' '}
                        under Pixels, then reconnect.
                      </Text>
                    </>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      This Snapchat account has no ad account we can read. Check that you authorized
                      the right organization, then reconnect.
                    </Text>
                  )}
                </BlockStack>
              </Banner>
            ) : (
              <ChoiceList
                title="Pick the pixel to send conversions to."
                choices={(assetsQuery.data?.pixels ?? []).map((p) => ({
                  label: `${p.pixelName} (${p.adAccountName} / ${p.orgName})`,
                  // The identifier, on its own line. It is the only value the
                  // merchant can match against Snapchat Events Manager - names
                  // repeat across ad accounts, and "Website Pixel" three times
                  // over is not a choice anyone can make confidently. It also
                  // lets them check, before committing, that this is the same
                  // id the connected card will display afterwards.
                  //
                  // Below the label rather than inside it: a 36-character UUID
                  // appended to a name plus an account plus an organisation
                  // makes the line unreadable. Pinterest and GA4 keep theirs
                  // inline because their identifiers are short.
                  helpText: p.pixelId,
                  value: p.pixelId,
                }))}
                selected={selectedPixel ? [selectedPixel] : []}
                onChange={(v) => setSelectedPixel(v[0] ?? '')}
              />
            )}
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title="Disconnect Snapchat Conversions API"
          primaryAction={{
            content: 'Disconnect',
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Are you sure you want to disconnect Snapchat Conversions API? Server-side tracking will stop
              immediately and conversion data will no longer be sent to Snapchat Ads.
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
